import React, { useEffect, useState } from 'react';
import { X, Keyboard } from 'lucide-react';

const KeyboardShortcuts = ({ onShortcut }) => {
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Show help with ?
      if (e.key === '?' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setShowHelp(prev => !prev);
        return;
      }

      // Alt + key shortcuts
      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'd':
            e.preventDefault();
            onShortcut?.('dashboard');
            break;
          case 's':
            e.preventDefault();
            onShortcut?.('stock');
            break;
          case 'o':
            e.preventDefault();
            onShortcut?.('orders');
            break;
          case 't':
            e.preventDefault();
            onShortcut?.('todo');
            break;
          case 'p':
            e.preventDefault();
            onShortcut?.('pricelist');
            break;
          case 'r':
            e.preventDefault();
            onShortcut?.('refresh');
            break;
          case 'n':
            e.preventDefault();
            onShortcut?.('new');
            break;
          default:
            break;
        }
      }

      // Escape to close modals
      if (e.key === 'Escape') {
        setShowHelp(false);
        onShortcut?.('escape');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onShortcut]);

  const shortcuts = [
    { keys: ['Alt', 'D'], description: 'Go to Dashboard' },
    { keys: ['Alt', 'S'], description: 'Go to Stock' },
    { keys: ['Alt', 'O'], description: 'Go to Orders' },
    { keys: ['Alt', 'T'], description: 'Go to To-Do' },
    { keys: ['Alt', 'P'], description: 'Go to Price List' },
    { keys: ['Alt', 'R'], description: 'Refresh Data' },
    { keys: ['Alt', 'N'], description: 'New Item/Task' },
    { keys: ['?'], description: 'Show/Hide Shortcuts' },
    { keys: ['Esc'], description: 'Close Modal/Menu' },
  ];

  if (!showHelp) {
    return (
      <button
        onClick={() => setShowHelp(true)}
        className="hidden lg:flex fixed bottom-4 left-4 items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 text-sm transition-colors z-40"
        title="Keyboard Shortcuts (?)"
      >
        <Keyboard className="w-4 h-4" />
        <span>Press ? for shortcuts</span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-slate-600" />
            <h3 className="text-lg font-semibold text-slate-800">Keyboard Shortcuts</h3>
          </div>
          <button
            onClick={() => setShowHelp(false)}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="space-y-3">
            {shortcuts.map((shortcut, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-slate-600">{shortcut.description}</span>
                <div className="flex gap-1">
                  {shortcut.keys.map((key, i) => (
                    <React.Fragment key={i}>
                      <kbd className="px-2 py-1 bg-slate-100 rounded text-sm font-mono text-slate-700 border border-slate-200">
                        {key}
                      </kbd>
                      {i < shortcut.keys.length - 1 && (
                        <span className="text-slate-400">+</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 rounded-b-xl border-t border-slate-200">
          <p className="text-sm text-slate-500 text-center">
            Press <kbd className="px-1.5 py-0.5 bg-white rounded text-xs border">Esc</kbd> to close
          </p>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcuts;
